import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { IRadioSelectProps as IRadioSelectPropsBase } from '@private/components';

import HintButton from 'components/HintButton';
import { appReactMemo } from 'hocs';

import StyledRadioSelect, { CustomLabel, Label } from './styled';

export interface IRadioSelectProps extends Omit<IRadioSelectPropsBase, 'onChange'> {
  hintText?: string;
  mapping?: Record<string, unknown>;
  maxHintWidth?: number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: unknown) => void;
}

const RadiosSelect = ({
  data,
  hintText,
  mapping = {},
  maxHintWidth,
  onChange,
  ...rest
}: IRadioSelectProps) => {
  const { i18n, t } = useTranslation();

  const renderCustomLabel = () => {
    if (hintText) {
      return (
        <CustomLabel>
          {rest.label && <Label>{rest.label}</Label>}
          <HintButton maxHintWidth={maxHintWidth}>{hintText}</HintButton>
        </CustomLabel>
      );
    }
  };

  const withTranslation = React.useMemo(() => {
    return data.map((item) => ({
      ...item,
      label: item.label && i18n.exists(item.label) ? t(item.label as never) : item.label,
    }));
  }, [data, i18n, t]);

  const onValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
      const reversedValue = mapping?.[value as keyof typeof mapping];
      const propertyExist = Object.prototype.hasOwnProperty.call(mapping, value);
      onChange?.(e, propertyExist ? reversedValue : value);
    },
    [mapping, onChange],
  );

  return (
    <StyledRadioSelect
      {...rest}
      customLabel={renderCustomLabel()}
      data={withTranslation}
      onChange={onValueChange}
    />
  );
};

export default appReactMemo(RadiosSelect);
